import { differenceInCalendarDays, differenceInSeconds, isToday, parse } from 'date-fns';
import {
  creditStatusTypes,
  idStatusTypes,
  REPORT_STATUS_AVAILABLE,
  shareStatusTypes,
  termsStatusTypes,
  vantageScoreExclusionStatusTypes,
} from '../constants/statusConstants';

const { TAC_CURRENTLY_ACCEPTED } = termsStatusTypes;

function timeUntil(timestamp) {
  const firstDate = new Date();
  const secondDate = parse(timestamp);
  return differenceInSeconds(secondDate, firstDate);
}

function getIsExpired(expiration) {
  return expiration ? timeUntil(expiration) < 0 : true;
}

export function getIsRenterIdVerified(renterIdStatus, renterIdExpiration) {
  return renterIdStatus === idStatusTypes.CURRENTLY_IDENTIFIED && !getIsExpired(renterIdExpiration);
}

export function getIsRenterIdVerifiedAndExpired(renterIdStatus, renterIdExpiration) {
  return renterIdStatus === idStatusTypes.CURRENTLY_IDENTIFIED && getIsExpired(renterIdExpiration);
}

export function getAvailableReportText({ expiration: shareExpiration }) {
  const expirationDate = parse(shareExpiration);
  const dateToday = new Date();

  if (isToday(expirationDate)) {
    return 'Expires today';
  } else {
    return `Expires in ${differenceInCalendarDays(expirationDate, dateToday)} days`;
  }
}

export function getNeedsToIdVerify({ termsStatus, isLandlordIdVerified } = {}) {
  return termsStatus !== TAC_CURRENTLY_ACCEPTED || !isLandlordIdVerified;
}

export function getApplicantFirstName(applicant) {
  if (!applicant) {
    return null;
  }
  const { firstName, contactInfo: { coapplicantFirstName } = {} } = applicant;

  return firstName || coapplicantFirstName;
}

export function getUnavailableReportText({
  hasComplianceError,
  screeningReportShareInfo: { status: shareStatus, expiration: shareExpiration } = {},
  reportStatus,
  isComplete = false,
  isDeclined = false,
} = {}) {
  const reportShared =
    (shareStatus === shareStatusTypes.ENABLED || shareStatus === shareStatusTypes.VIEWED) &&
    !getIsExpired(shareExpiration);
  const reportAvailable =
    reportStatus === REPORT_STATUS_AVAILABLE || vantageScoreExclusionStatusTypes.includes(reportStatus);

  if (hasComplianceError) {
    return 'Report temporarily unavailable';
  }

  if (!isComplete) {
    return 'Report unavailable';
  }

  if (reportAvailable && getIsExpired(shareExpiration)) {
    return 'Report expired';
  }

  if (reportStatus === creditStatusTypes.NO_RECORD_FOUND) {
    return 'Experian is unable to provide a credit report because no records exist to create the report';
  }

  if (!reportShared || !reportAvailable || isDeclined) {
    return 'Report unavailable';
  }
}

export function getShowNavLinkAsButton({
  icraaAcceptedForApplicant = false,
  isIcraaRequired = true,
  needsToIdVerify,
  customShowNavLinkAsButton,
} = {}) {
  const needsToAcceptIcraa = isIcraaRequired && !icraaAcceptedForApplicant;
  return (
    customShowNavLinkAsButton ||
    // If ICRAA has not been accepted, show as button unless landlord needs to ID verify.
    (needsToAcceptIcraa && !needsToIdVerify)
  );
}

export function getDisplayName(firstName, middleName, lastName) {
  let displayName;

  if (firstName || lastName) {
    const name =
      (firstName && firstName.length > 0 ? `${firstName} ` : '') +
      (middleName && middleName.length > 0 ? `${middleName} ` : '') +
      (lastName && lastName.length > 0 ? `${lastName} ` : '');

    displayName = name.length > 0 ? name.slice(0, -1) : '';
  } else {
    displayName = '';
  }

  return displayName;
}
